import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

interface Photo {
  url: string;
  caption?: string;
  category?: string;
  uploadedAt: string;
}

interface PhotoLightboxProps {
  photos: Photo[];
  initialIndex: number;
  onClose: () => void;
}

const PhotoLightbox: React.FC<PhotoLightboxProps> = ({ photos, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const lightboxRef = useRef<HTMLDivElement>(null);

  const currentPhoto = photos[currentIndex];
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < photos.length - 1;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrevious) {
        goToPrevious();
      } else if (e.key === 'ArrowRight' && hasNext) {
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [currentIndex, hasPrevious, hasNext, onClose]);

  const goToPrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const goToNext = () => {
    if (hasNext) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === lightboxRef.current) {
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div
      ref={lightboxRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors z-10"
        aria-label="Close lightbox"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Previous Button */}
      {hasPrevious && (
        <button
          onClick={goToPrevious}
          className="absolute left-4 p-3 text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors z-10"
          aria-label="Previous photo"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      {/* Next Button */}
      {hasNext && (
        <button
          onClick={goToNext}
          className="absolute right-4 p-3 text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors z-10"
          aria-label="Next photo"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Image Container */}
      <div className="max-w-6xl max-h-[85vh] w-full flex flex-col">
        {/* Image */}
        <div className="flex-1 flex items-center justify-center mb-4">
          <img
            src={currentPhoto.url}
            alt={currentPhoto.caption || 'Photo'}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>

        {/* Photo Info */}
        <div className="bg-white bg-opacity-10 rounded-lg p-4 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              {currentPhoto.caption && (
                <p className="text-lg mb-2">{currentPhoto.caption}</p>
              )}
              <div className="flex items-center gap-4 text-sm text-gray-300">
                {currentPhoto.category && (
                  <span className="px-2 py-1 bg-white bg-opacity-20 rounded capitalize">
                    {currentPhoto.category}
                  </span>
                )}
                {currentPhoto.uploadedAt && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(currentPhoto.uploadedAt)}
                  </div>
                )}
              </div>
            </div>
            <div className="text-sm text-gray-300">
              {currentIndex + 1} / {photos.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoLightbox;